import React, { useMemo } from 'react';

const SplitRow = ({ label, stat, isPitcher }) => {
  if (!stat) return null;
  return (
    <div className="flex items-center gap-2 py-2 border-b border-[rgba(101,141,113,0.2)] last:border-0">
      <span className="text-[9px] uppercase tracking-widest text-[#658d71] font-bold w-24 shrink-0">{label}</span>
      {!isPitcher ? (
        <div className="flex gap-3 flex-wrap">
          {[['AVG', stat.avg], ['OBP', stat.obp], ['SLG', stat.slg], ['OPS', stat.ops], ['HR', stat.homeRuns], ['RBI', stat.rbi]].map(([k, v]) => (
            <div key={k} className="flex flex-col items-center border border-[rgba(101,141,113,0.3)] px-2 py-1 bg-[rgba(0,0,0,0.1)] min-w-[36px]">
              <span className="text-[8px] text-[#658d71] uppercase tracking-widest font-bold">{k}</span>
              <span className="text-[10px] font-black text-white tabular-nums">{v ?? '--'}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex gap-3 flex-wrap">
          {[['ERA', stat.era], ['WHIP', stat.whip], ['K', stat.strikeOuts], ['IP', stat.inningsPitched], ['BB', stat.baseOnBalls]].map(([k, v]) => (
            <div key={k} className="flex flex-col items-center border border-[rgba(101,141,113,0.3)] px-2 py-1 bg-[rgba(0,0,0,0.1)] min-w-[36px]">
              <span className="text-[8px] text-[#658d71] uppercase tracking-widest font-bold">{k}</span>
              <span className="text-[10px] font-black text-white tabular-nums">{v ?? '--'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PlayerMetricsSplits = ({ metrics, isPitcher }) => {
  const splits = useMemo(() => {
    const group = isPitcher ? 'pitching' : 'hitting';
    const allSplits = metrics?.splits?.stats?.find(s => s.group?.displayName === group)?.splits || [];
    const map = {};
    allSplits.forEach(s => { map[s.split?.code || s.split?.description] = s.stat; });
    return map;
  }, [metrics, isPitcher]);

  const splitDefs = [
    { label: 'Home', codes: ['h', 'Home'] },
    { label: 'Away', codes: ['a', 'Away'] },
    { label: 'vs LHP', codes: ['vl', 'vs Left'] },
    { label: 'vs RHP', codes: ['vr', 'vs Right'] },
  ];

  const getSplit = (codes) => {
    for (const c of codes) {
      if (splits[c]) return splits[c];
    }
    return null;
  };

  const hasSplits = splitDefs.some(d => getSplit(d.codes));

  return (
    <div className="flex flex-col gap-4">
      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">
            Season Splits — {new Date().getFullYear()}
          </span>
          <p className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest mt-0.5">
            Home / Away · vs Left / vs Right
          </p>
        </div>
        <div className="p-3">
          {hasSplits ? (
            splitDefs.map(def => (
              <SplitRow key={def.label} label={def.label} stat={getSplit(def.codes)} isPitcher={isPitcher} />
            ))
          ) : (
            <div className="text-[#658d71] text-xs text-center py-8 uppercase tracking-widest">
              No split data available for this season
            </div>
          )}
        </div>
      </div>

      {!hasSplits && (
        <div className="border border-[rgba(101,141,113,0.3)] p-4 bg-[rgba(0,0,0,0.05)] text-center">
          <p className="text-[9px] text-[#658d71] uppercase tracking-widest font-bold">
            Split data is typically available mid-season. Check back later.
          </p>
        </div>
      )}
    </div>
  );
};

export default PlayerMetricsSplits;